import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {combineLatest, filter, firstValueFrom, Observable, Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {LearningPackageStudyProgressResponseDto} from '../../types/LearningPackageStudyProgressResponseDto';
import {
    AuthUntilUserRoleService
} from '../../../../../micromate-learn-web/src/app/core/authentication/auth-until-user-role.service';
import {PopupOptions} from '../popup/PopupOptions';
import {InsightPopupComponent} from './insight-popup/insight-popup.component';
import {InsightPopupData} from './insight-popup/InsightPopupData';
import {PopupService} from '../popup/popup.service';
import {InsightTabs} from './insight-popup/InsightTabs';
import {TAB_NAVIGATION_SERVICE, TabNavigationService} from '../../services/TabNavigationService';
import {INSIGHT_POPUP_NAME, OpenInsightPopupData} from './insight-popup/OpenInsightPopupData';
import {OpenPopupData} from '../../../../../micromate-learn-web/src/app/core/OpenPopupData';
import {InsightPopupTabs} from './insight-popup/InsightPopupTabs';
import {BotatoChatManagerService, CommunicationService} from 'botato-angular-lib';

@Component({
    selector: 'mm-study-progress',
    templateUrl: './study-progress.component.html',
    styleUrls: ['./study-progress.component.scss'],
    standalone: false
})
export class StudyProgressComponent implements OnInit, OnDestroy {

    @Input({required: true})
    public studyProgressesData!: Observable<LearningPackageStudyProgressResponseDto[] | undefined>;

    @Input({required: true})
    public darkMode!: boolean;

    @Input()
    public changDetectionAfterTurn: boolean | undefined;

    @Output()
    public learnPackageActivated = new EventEmitter<string>();

    public studyProgresses: LearningPackageStudyProgressResponseDto[] | undefined;
    public isDataLoaded = false;
    public learningGoalsRange = [
        {
            value: 90,
            legend: 'expert'
        },
        {
            value: 80,
            legend: 'professional'
        },
        {
            value: 70,
            legend: 'skilled'
        },
        {
            value: 55,
            legend: 'advanced'
        },
        {
            value: 40,
            legend: 'beginners'
        },
        {
            value: 0,
            legend: 'newbie'
        }
    ];
    private subscription: Subscription | undefined;
    public isStartingLearningSessionInProgress: boolean = false;

    public isAdmin$: Observable<boolean>;

    private readonly POPUP_TO_INSIGHT_TAB_MAP: Record<InsightPopupTabs, InsightTabs> = {
        [InsightPopupTabs.DETAILS]: InsightTabs.DETAILS,
        [InsightPopupTabs.FEEDBACK]: InsightTabs.FEEDBACK,
        [InsightPopupTabs.QUESTIONS]: InsightTabs.QUESTIONS,
        [InsightPopupTabs.LEARNING_HISTORY]: InsightTabs.LEARNING_HISTORY,
        [InsightPopupTabs.SKILLS_PROFILE]: InsightTabs.SKILLS_PROFILE,
        [InsightPopupTabs.TOPICS]: InsightTabs.TOPICS
    };

    constructor(private ref: ChangeDetectorRef,
                public roleService: AuthUntilUserRoleService,
                public translate: TranslateService,
                private popupService: PopupService,
                public  botatoChatManager: BotatoChatManagerService,
                private communicationService: CommunicationService,
                @Inject(TAB_NAVIGATION_SERVICE) private tabNavigationService: TabNavigationService) {
        this.isAdmin$ = roleService.assertAdmin();
    }

    public ngOnInit(): void {
        this.subscription = combineLatest([this.studyProgressesData, this.tabNavigationService.openPopup$]).pipe(
            filter((data): data is [LearningPackageStudyProgressResponseDto[], OpenPopupData | undefined] => data[0] !== undefined))
            .subscribe(async ([studyProgressesData, openPopup]) => {
                this.isDataLoaded = true;
                this.studyProgresses = studyProgressesData.map(studyProgress => {
                    studyProgress.studyProgressScaleName = this.getUserScaleName(studyProgress.studyProgress);
                    return studyProgress;
                });

                this.ref.markForCheck();

                if (openPopup !== undefined && openPopup.popupName === INSIGHT_POPUP_NAME) {
                    await this.openPopup(openPopup as OpenInsightPopupData);
                }
            })
    }


    public ngOnDestroy(): void {
        if (this.subscription !== undefined) {
            this.subscription.unsubscribe();
        }
    }

    private async openPopup(popupInfo: OpenInsightPopupData): Promise<void> {
        const popupData = popupInfo.data;
        const studyProgress = this.studyProgresses?.find(data => data.learningPackageId === popupData.learningPackageName);
        const insightTab = this.POPUP_TO_INSIGHT_TAB_MAP[popupData.insightPopupPage];
        if (insightTab === undefined) {
            throw new Error(`Unknown path: ${popupData.insightPopupPage}`);
        }
        if (studyProgress === undefined) {
            throw new Error(`Unknown learning package: ${popupData.learningPackageName}`);
        }

        await this.showInsightPopup(studyProgress, insightTab);
    }

    public openInsightPopup(studyProgress: LearningPackageStudyProgressResponseDto): void {
        this.tabNavigationService.navigateToInsightPopup(studyProgress.learningPackageId);
    }

    public async showInsightPopup(studyProgress: LearningPackageStudyProgressResponseDto, currentTab: InsightTabs): Promise<void> {
        if (!await firstValueFrom(this.popupService.isPopupOpen$)) {
            const popupOptions = new PopupOptions('custom', 'closable', 1000, 800);
            const insightPopupData = new InsightPopupData(currentTab, studyProgress);
            await this.popupService.open<InsightPopupData>(InsightPopupComponent, insightPopupData, popupOptions);
            this.tabNavigationService.navigateToStudyProgress(false);
        }
    }

    private getUserScaleName(studyProgress: number | undefined): string | undefined {
        if (studyProgress === undefined) {
            return undefined;
        }
        return this.learningGoalsRange.filter(range => studyProgress >= range.value)[0]?.legend;
    }

    public learnPackage(learningPackageId: string): void {
        this.tabNavigationService.navigateToChat(false);
        this.communicationService.messageSendingStarted.emit();
        this.botatoChatManager.showTypingIndicator();
        this.learnPackageActivated.emit(learningPackageId);
        this.isStartingLearningSessionInProgress = true;
    }
}
